import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { DebtorCandidate } from '../tokens/debtor-directory.token';
import {
  LenderPreApprovedDebtorsApi,
  PreApprovedDebtor,
  PreApprovedDebtorAddRequest,
  PreApprovedDebtorFilters,
} from '../tokens/lender-preapproved-debtors.token';

/** List envelope — the resource wraps the rows in `{ data: [...] }`, like the blacklist surface. */
interface PreApprovedDebtorListEnvelope {
  data: PreApprovedDebtor[];
}

/**
 * REST-only implementation of {@link LenderPreApprovedDebtorsApi} (EM-1752, story EM-1671).
 *
 * Thin `HttpClient` wrapper over the live `LenderPreApprovedDebtorResource` at
 * `/services/embedded/api/v1/lender/preapproved-debtors`. No mock, no fallback (Rules 07/10); every
 * row is lender-scoped server-side (Rule 22 shape). The single home for these endpoints — the SM page
 * and the RM read-only view both consume it through the `LENDER_PREAPPROVED_DEBTORS` token.
 */
@Injectable()
export class LenderPreApprovedDebtorsRestService implements LenderPreApprovedDebtorsApi {
  private readonly http = inject(HttpClient);

  list(filters?: PreApprovedDebtorFilters): Observable<PreApprovedDebtor[]> {
    let params = new HttpParams();
    if (filters?.q) params = params.set('q', filters.q);
    if (filters?.product) params = params.set('product', filters.product);
    if (filters?.status) params = params.set('status', filters.status);
    return this.http
      .get<PreApprovedDebtorListEnvelope>(API_PATHS.LENDER_PREAPPROVED_DEBTORS, { params })
      .pipe(map(env => env?.data ?? []));
  }

  lookup(cui: string): Observable<DebtorCandidate> {
    const params = new HttpParams().set('cui', (cui ?? '').trim());
    return this.http.get<DebtorCandidate>(`${API_PATHS.LENDER_PREAPPROVED_DEBTORS}/lookup`, { params });
  }

  add(request: PreApprovedDebtorAddRequest): Observable<PreApprovedDebtor> {
    return this.http.post<PreApprovedDebtor>(API_PATHS.LENDER_PREAPPROVED_DEBTORS, request);
  }

  update(id: number, request: PreApprovedDebtorAddRequest): Observable<PreApprovedDebtor> {
    return this.http.put<PreApprovedDebtor>(`${API_PATHS.LENDER_PREAPPROVED_DEBTORS}/${id}`, request);
  }

  softRemove(id: number): Observable<PreApprovedDebtor> {
    return this.http.delete<PreApprovedDebtor>(`${API_PATHS.LENDER_PREAPPROVED_DEBTORS}/${id}`);
  }

  restore(id: number): Observable<PreApprovedDebtor> {
    return this.http.post<PreApprovedDebtor>(`${API_PATHS.LENDER_PREAPPROVED_DEBTORS}/${id}/restore`, {});
  }
}
